/**
 * The encounter: N aircraft advanced through one deterministic tick.
 *
 * `SimState`/`step` stay exactly what they were — one aeroplane, one pure
 * function — and this is the layer above that owns everything an aeroplane
 * cannot know about on its own: who else is in the sky, whose rounds hit whom,
 * and whether the fight is over. `createSim`/`step` are untouched and still the
 * right API for a solo flight.
 *
 * Determinism (AD-007a) holds by construction:
 *
 * - the roster is an ordered list and every pass walks it in that order, so the
 *   sequence of random draws depends only on the setup, never on iteration
 *   order or object identity;
 * - each aircraft's *own* physics randomness comes from its own bank, seeded
 *   `fnv1a32(id, encounterSeed)`, so adding a third aeroplane cannot change
 *   what the first two's jam and overstress rolls do;
 * - all *combat* randomness comes from the encounter's shared bank, on the
 *   `gunnery` and `ai` streams only.
 */

import { FLAT_GROUND, type GroundQuery } from "../flight/ground.ts";
import { NEUTRAL_INPUTS, type FlightInputs } from "../flight/inputs.ts";
import type { Vec3 } from "../flight/math3d.ts";
import { RngBank, type RngBankState } from "../flight/rng.ts";
import type { SimOptions, SimState } from "../flight/state.ts";
import { FIXED_DT } from "../flight/units.ts";
import type { AiPilotState } from "./ai-pilot.ts";
import type { FireState } from "./combat-damage.ts";
import { ENCOUNTER_SIDES, type EncounterSide } from "./encounter-sides.ts";
import { advanceRoster, type RosterTick } from "./encounter-tick.ts";
import { combatEvent, type CombatEvent } from "./events.ts";
import { encounterStatus, isOver, type AircraftStatus, type EncounterStatus } from "./outcome.ts";
import { advanceParachute } from "./bail-command.ts";
import { parachuteAloft, type ParachuteState } from "./parachute.ts";
import type { RouteOrders, RouteState, Waypoint } from "./route.ts";
import type { AiSkill } from "./skill.ts";
import type { AmmunitionType, EncounterTarget, EncounterTargetSpec } from "./targets.ts";
import { isStrategicTargetKind } from "./target-profiles.ts";
import type { BombImpact, BombRecord } from "./bombs.ts";
import type { CombatLevel, FlakBattery, FlakBatterySpec, FlakBurst } from "./flak.ts";
import type { RoundRecord } from "./rounds.ts";
import { NO_SCENERY, type SceneryFire, type SceneryQuery } from "./scenery-fires.ts";
import type { CrashSite } from "./crash-sites.ts";
import type { ShipSpec } from "./ship-deployment.ts";
import type { ShipAim } from "./ship-pass.ts";

export { ENCOUNTER_SIDES };
export type { EncounterSide };

export type EncounterPilot = { readonly kind: "player" } | { readonly kind: "ai"; readonly skill: AiSkill };

export interface EncounterAircraftSpec {
  /** Stable across the whole encounter; the roster is keyed and ordered by it. */
  readonly id: string;
  readonly side: EncounterSide;
  readonly flight: SimOptions;
  readonly pilot: EncounterPilot;
  /**
   * Added to the position `createSim` produces (which is always the world
   * origin at `flight.altitudeM`), so a spawn can put an aeroplane 800 m astern
   * and 200 m above without the flight core needing a placement concept.
   */
  readonly offsetM?: Vec3;
  /**
   * Where this flight is going when nobody is shooting at it (`route.ts`).
   * Omitted for a pure dogfighter, which has nowhere to be but here.
   */
  readonly waypoints?: readonly Waypoint[];
  /** Whether this flight may abandon its route to fight. Defaults to `engage`. */
  readonly routeOrders?: RouteOrders;
  /** Belt loaded for this sortie; only balloons and Zeppelins can tell the difference. */
  readonly ammunition?: AmmunitionType;
  /** What this flight is carrying under the wings, and what it is carrying it to. Omitted for a fighter. */
  readonly bombs?: BombLoadSpec;
  /**
   * Static targets this flight was *sent* to attack, in preference order —
   * the balloons a `BALLOON DEFENSE` raid is after. A flight with none picks
   * its opponents the way a scout always has: the nearest aeroplane.
   */
  readonly strikeTargetIds?: readonly string[];
}

/**
 * A bomb load. `targetIds` is the raid's aiming list, not a kill list: the
 * bomber aims at the first one still standing, which is what makes a stick of
 * three heavies over two sheds spread itself sensibly instead of cratering the
 * same wreck three times.
 */
export interface BombLoadSpec {
  readonly count: number;
  readonly targetIds: readonly string[];
}

export interface EncounterSetup {
  readonly seed: number;
  readonly aircraft: readonly EncounterAircraftSpec[];
  /** Balloons and Zeppelins — shot at, but not flown (`targets.ts`). */
  readonly targets?: readonly EncounterTargetSpec[];
  /** Anti-aircraft emplacements — they shoot, but are not shot at (`flak.ts`). */
  readonly flak?: readonly FlakBatterySpec[];
  /**
   * The Realism Panel's COMBAT LEVEL (AD-010 §9.4) — player-only, and today
   * only flak reads it. Fixed for the whole encounter, the same way `seed` is:
   * a lockstep duel that never sets it (duels have no flak yet) still agrees
   * with its peer trivially, because `undefined` on both sides resolves to the
   * same full-damage default (`flak.ts`'s `combatLevelScale`).
   */
  readonly combatLevel?: CombatLevel;
  /**
   * The ships at sea — **ENHANCED SIMULATION's whole footprint on the setup**
   * (`ship-deployment.ts`).
   *
   * Absent on every encounter this port built before the switch existed and on
   * every one flown with it off, which is what makes the feature bit-zero: with
   * no ships there is no hull on the target list, no battery on the flak list,
   * no draw on any stream and not one changed byte in `encounterHash`.
   */
  readonly ships?: readonly ShipSpec[];
}

export interface CombatAircraft {
  readonly id: string;
  readonly side: EncounterSide;
  readonly sim: SimState;
  /** `null` for the player. */
  readonly ai: AiPilotState | null;
  readonly fire: FireState;
  readonly status: AircraftStatus;
  /**
   * How long this aircraft has been out of control, in seconds (AD-014). Zero
   * for anything still flying. It is what the fall timeout counts against, and
   * a renderer may read it to know how long a wreck has been going down.
   */
  readonly fallingSeconds: number;
  /** Running total of rounds fired, which is what a round's ordinal counts against. */
  readonly roundsFired: number;
  readonly hitsTaken: number;
  /**
   * The gunfire half of `hitsTaken` — rounds from another aeroplane's guns
   * only, with archie left out. `damage.c` skips the cockpit's own hit
   * overlays for damage kinds 4 and 5 (`aa.c`'s two flak kinds), so "have I
   * been shot at by an aeroplane" is a different question from "have I been
   * hit", and the painted cockpit asks this one
   * (`apps/web/src/flight/cockpit-hits.ts`).
   */
  readonly gunHitsTaken: number;
  readonly hitsScored: number;
  /** True on any tick this aircraft took rounds — the AI's own "I am under fire". */
  readonly underFire: boolean;
  /**
   * Who last put rounds into it. This is what makes kill credit correct for the
   * usual case: an aeroplane shot to pieces spins away and hits the ground
   * several seconds later, on a tick nobody fired, so the destroying tick has
   * no shooter of its own to name.
   */
  readonly lastAttackerId: string | null;
  /** Navigation, advanced every tick; `null` for a flight with no route. */
  readonly route: RouteState | null;
  readonly routeOrders: RouteOrders;
  readonly ammunition: AmmunitionType;
  /** Bombs still on the rack. */
  readonly bombsRemaining: number;
  /** Bombs already gone — what "this bomber got through to the target" is counted from. */
  readonly bombsReleased: number;
  /** The aiming list this flight was given, in preference order. */
  readonly bombTargetIds: readonly string[];
  /** Tick the last bomb of the stick left the rack, or `null` while the load is untouched. */
  readonly lastReleaseTick: number | null;
  readonly strikeTargetIds: readonly string[];
  /**
   * What this pilot is currently after — an aircraft id or a static-target id.
   * Recorded rather than recomputed because target *choice* needs hysteresis: a
   * balloon-burner with a defender hovering at the engage range would otherwise
   * swap quarry every tick and fly neither attack.
   */
  readonly quarryId: string | null;
}

export interface EncounterState {
  readonly seed: number;
  readonly tick: number;
  readonly timeS: number;
  readonly aircraft: readonly CombatAircraft[];
  readonly targets: readonly EncounterTarget[];
  readonly flak: readonly FlakBattery[];
  /** The shared combat bank: `gunnery`, `ai` and `flak` draws only. */
  readonly rng: RngBankState;
  readonly rounds: readonly RoundRecord[];
  /** Bombs in the air (`bombs.ts`), on the same terms the rounds are. */
  readonly bombs: readonly BombRecord[];
  /** Bombs that went off on *this* tick, with where. Cleared next tick, exactly like `events`. */
  readonly bombImpacts: readonly BombImpact[];
  /**
   * Buildings that have been strafed and are burning (AD-018, `scenery-fires.ts`).
   *
   * **Sparse**: one entry per structure actually shot, never one per placed
   * building, and empty — the common case, and the only case without an injected
   * `SceneryQuery` — for every sortie in which nobody strafed a village.
   */
  readonly sceneryFires: readonly SceneryFire[];
  /**
   * Where aeroplanes have gone in (AD-014c, `crash-sites.ts`).
   *
   * **Sparse and cumulative**: one entry per aeroplane that actually reached the
   * ground, capped at `MAX_CRASH_SITES`, empty — the common case for most of a
   * sortie — until one does. Unlike `flakBursts` these *are* carried between
   * ticks, because a scorched field is state and the scar outlives its fire.
   */
  readonly crashSites: readonly CrashSite[];
  /**
   * The bursts that went off on this tick, for the renderer. Not carried
   * between ticks — `flak.ts` resolves a shell at the moment it is created, so
   * there is nothing in flight to remember.
   */
  readonly flakBursts: readonly FlakBurst[];
  readonly events: readonly CombatEvent[];
  readonly status: EncounterStatus;
  /** Carried unchanged from `EncounterSetup` — see its own doc. */
  readonly combatLevel?: CombatLevel;
  /**
   * The player under a canopy, once he has jumped (AD-021, `parachute.ts`).
   *
   * **`undefined` and not a null object**, so `canonicalJson` drops the key and
   * an encounter nobody bailed out of hashes to exactly the string it did
   * before this field existed — the idiom `sceneryFires` and `crashSites` use.
   * One canopy and not a list because **AI pilots do not bail** in this port;
   * the design doc costs that follow-on.
   */
  readonly parachute?: ParachuteState;
  /**
   * The ships, carried unchanged from the setup. **Immutable**: a patrol is a
   * shape, and where a ship is at any tick is `patrolPoseAt(patrol, timeS)` —
   * so there is nothing here for a tick to write and nothing for the hash to
   * carry that her hull's own position does not already say.
   *
   * `[]` for every encounter flown without ENHANCED SIMULATION.
   */
  readonly ships: readonly ShipSpec[];
  /**
   * Where each ship's five mountings are pointing on this tick, for the
   * renderer to swing the rig by.
   *
   * An **output**, not state — the same standing `flakBursts` has, and for the
   * same reason: it is recomputed from the poses every tick, nothing carries it
   * forward, and `encounter-hash.ts` does not look at it. Empty whenever there
   * are no ships.
   */
  readonly shipAims: readonly ShipAim[];
}

// Opening an encounter is : turning a setup into the first
// tick's state is a different job from stepping one, and this file was past the
// size ceiling. Re-exported so no consumer moved.
export { createEncounter } from "./encounter-open.ts";

/**
 * A side with nothing left flying — and no balloon, Zeppelin or standing
 * objective — has lost; a side with nothing in the encounter at all was never
 * in the fight.
 *
 * Only *strategic* targets are counted. A battery's searchlights are on the
 * roster and can be shot out (`searchlight-posts.ts`), but a war is not still
 * being fought because somebody's lamp is unbroken, and counting them would
 * quietly make every night mission unwinnable until the player had strafed
 * every light on the map.
 */
function sideStanding(state: RosterTick, side: EncounterSide): boolean {
  const aircraft = state.aircraft.filter((entry) => entry.side === side);
  const targets = state.targets.filter(
    (entry) => entry.side === side && isStrategicTargetKind(entry.kind),
  );
  if (aircraft.length + targets.length === 0) return true;
  return (
    aircraft.some((entry) => entry.status !== "destroyed") || targets.some((entry) => !entry.destroyed)
  );
}

function outcomeEvents(
  previous: EncounterStatus,
  next: EncounterStatus,
  aircraft: readonly CombatAircraft[],
): CombatEvent[] {
  if (previous === next || !isOver(next)) return [];
  const player = aircraft.find((entry) => entry.ai === null);
  const id = player?.id ?? aircraft[0]?.id ?? "player";
  if (next === "enemyDown") return [combatEvent("encounterWon", id)];
  return [combatEvent("encounterLost", id)];
}

export function aircraftById(state: EncounterState, id: string): CombatAircraft | undefined {
  return state.aircraft.find((entry) => entry.id === id);
}

/** The player's aircraft — the one with no AI pilot. */
export function playerAircraft(state: EncounterState): CombatAircraft | undefined {
  return state.aircraft.find((entry) => entry.ai === null);
}

export function hostileAircraft(state: EncounterState): readonly CombatAircraft[] {
  return state.aircraft.filter((entry) => entry.side === "hostile");
}

export function targetById(state: EncounterState, id: string): EncounterTarget | undefined {
  return state.targets.find((entry) => entry.id === id);
}

/** Balloons and Zeppelins still up, on either side. */
export function liveTargets(state: EncounterState): readonly EncounterTarget[] {
  return state.targets.filter((entry) => !entry.destroyed);
}

/**
 * Advances every aircraft one fixed tick. `inputs` is keyed by aircraft id and
 * only consulted for player-flown aircraft; an id with no entry flies with the
 * stick centred.
 */
export function stepEncounter(
  state: EncounterState,
  inputs: Readonly<Record<string, FlightInputs>> = {},
  dt: number = FIXED_DT,
  ground: GroundQuery = FLAT_GROUND,
  scenery: SceneryQuery = NO_SCENERY,
): EncounterState {
  const rng = new RngBank(state.rng);
  const tick = state.tick + 1;
  const roster: RosterTick = advanceRoster({
    aircraft: state.aircraft,
    targets: state.targets,
    flak: state.flak,
    ships: state.ships,
    timeS: state.timeS,
    rounds: state.rounds,
    bombs: state.bombs,
    sceneryFires: state.sceneryFires,
    crashSites: state.crashSites,
    playerInputs: (id) => inputs[id] ?? NEUTRAL_INPUTS,
    rng,
    tick,
    dt,
    ground,
    scenery,
    combatLevel: state.combatLevel,
  });

  const parachute = advanceParachute(state.parachute, ground, dt);

  const status = encounterStatus({
    // AD-021: **not down while he is still in the air.** His aeroplane may
    // already be a burning write-off — that is what `sideStanding` sees — but
    // ending here would freeze the sim at the moment of the jump.
    playerAlive: sideStanding(roster, "friendly") || parachuteAloft(parachute),
    enemyAlive: sideStanding(roster, "hostile"),
  });

  return {
    seed: state.seed,
    tick,
    timeS: state.timeS + dt,
    aircraft: roster.aircraft,
    targets: roster.targets,
    flak: roster.flak,
    flakBursts: roster.flakBursts,
    rng: rng.snapshot(),
    // Already pruned, already swept for collisions, already capped —
    // `advanceRoster`'s shoot pass owns the live list end to end now that the
    // rounds in it are what hits are resolved against.
    rounds: roster.rounds,
    bombs: roster.bombs,
    bombImpacts: roster.bombImpacts,
    sceneryFires: roster.sceneryFires,
    crashSites: roster.crashSites,
    events: [...roster.events, ...outcomeEvents(state.status, status, roster.aircraft)],
    status,
    combatLevel: state.combatLevel,
    parachute,
    ships: state.ships,
    shipAims: roster.shipAims,
  };
}

/** Every flight carrying bombs — the raid, as the mission layer counts it. */
export function bomberAircraft(state: EncounterState): readonly CombatAircraft[] {
  return state.aircraft.filter((entry) => entry.bombsRemaining > 0 || entry.bombsReleased > 0);
}
